/**
 * landingCurated.js: the static copy behind the LandingPage sections.
 *
 * # Concierge mode (2026-10-09)
 * Xpert launches as a concierge service: every review is done by a named
 * human, and today that is the founder. This file used to hold hand-written
 * marketing examples that read like platform data: "earlier today" verified
 * reviews, curated badge tiles, a fallback bench of invented experts and
 * placeholder testimonials. They were deleted, not softened. CLAUDE.md:
 * "Never invent stats." `landingCurated.test.js` fails if any of them
 * comes back.
 *
 * What is left is product description only: what you send, what you get
 * back, who reviews it. Prices are not here; they come from
 * `src/lib/reviewPricing.js`, which mirrors the backend.
 *
 * # What is live on the landing page
 *   - BadgeWall reads `/api/verifications/recent` (public badges only). It
 *     has no fallback: when the feed is empty it shows an empty state.
 */

// ---------------------------------------------------------------------------
// Hero microtrust strip: "Works with output from <tool>".
// The AI tools whose output we accept for review. Not integrations or
// partnerships. Limited to tools whose output is code or product/strategy
// writing, which is what gets reviewed today.
// ---------------------------------------------------------------------------
export const TRUST_TOOLS = [
  "ChatGPT",
  "Claude",
  "Cursor",
  "Gemini",
  "Copilot",
];

// ---------------------------------------------------------------------------
// Hero aside: what a buyer gets back. Describes the verdict fields that exist
// (`VerificationVerdictCreate` in backend/routes/verifications.py). It is a
// description, not a sample verdict.
// ---------------------------------------------------------------------------
export const VERDICT_CONTENTS = [
  "A written verdict from a named reviewer",
  "Key findings, with a confidence level",
  "What to change, as a list of action items",
  "Files back when useful: a patch or annotated screenshots",
  "A badge that stays private unless you publish it",
];

// ---------------------------------------------------------------------------
// How it works: three numbered steps.
// ---------------------------------------------------------------------------
export const HOW_STEPS = [
  {
    n: "1",
    title: "Send the work and the chat",
    body: "Paste the code, spec or memo, plus the AI conversation that produced it. Pick a turnaround: 24 hours, 48 hours or one week.",
  },
  {
    n: "2",
    title: "A named reviewer reads the brief",
    body: "Your AI conversation comes along as the brief, so the review starts where you got stuck instead of from zero.",
  },
  {
    n: "3",
    title: "Get a written verdict",
    body: "Findings, what to change and how confident the reviewer is, within the turnaround you picked. The badge stays private unless you publish it.",
  },
];

// ---------------------------------------------------------------------------
// Who reviews your work: the two kinds of work the founder reviews today
// (founder decision 2026-10-07). Add an area only when a named reviewer
// covers it.
// ---------------------------------------------------------------------------
export const REVIEW_AREAS = [
  {
    name: "AI-built code",
    sub: "Apps, APIs, integrations, deploys and migrations written with Cursor, Claude Code, Copilot or ChatGPT.",
  },
  {
    name: "AI-built product and strategy work",
    sub: "PRDs, positioning, pricing and go-to-market plans drafted with an AI model.",
  },
];

// ---------------------------------------------------------------------------
// How the founder reviews (founder policies logged 2026-10-08, plus the
// private-by-default badge shipped in #45).
// ---------------------------------------------------------------------------
export const REVIEW_PRINCIPLES = [
  {
    title: "AI tools plus human judgement",
    body: "He uses AI tools while he reviews and says so. The calls in the verdict are his.",
  },
  {
    title: "Private by default",
    body: "The verdict is yours. A public badge goes up only if you agree to it.",
  },
  {
    title: "NDA on request",
    body: "If you need one before you send anything, Xpert signs your NDA.",
  },
];
